import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './Sheet.css';
import DataGrid from './DataGrid';
import PageHeader from './PageHeader';
import { useUserStorage } from '../services/userDataStorage';
import { usePostHog } from 'posthog-js/react';
import TemplateModal from './TemplateModal';

interface SheetProps {
  onSidebarRefresh: () => void;
  sidebarVisible: boolean;
  onShowSidebar: () => void;
  onSubscriptionRefresh?: () => Promise<void>;
}

interface SelectedCellInfo {
  rowIndex: number;
  content: string;
  columnId: string | number;
}

const Sheet: React.FC<SheetProps> = ({ onSidebarRefresh, sidebarVisible, onShowSidebar, onSubscriptionRefresh }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userStorage = useUserStorage();
  const posthog = usePostHog();
  
  const [gridId, setGridId] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [dataReady, setDataReady] = useState<boolean>(false);
  
  // Undo/Redo state management
  const undoHandlerRef = useRef<(() => void) | null>(null);
  const redoHandlerRef = useRef<(() => void) | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Selection state management
  const [selectedRowsCount, setSelectedRowsCount] = useState<number>(0);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [columnConfigs, setColumnConfigs] = useState<any[]>([]);

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);

  // Load grid data
  useEffect(() => {
    const loadGrid = async () => {
      if (!id) {
        navigate('/home');
        return;
      }

      if (!userStorage?.isFirebaseReady) {
        return;
      }

      setLoading(true);
      
      try {
        // Try to get from cache first for instant loading
        const grid = await userStorage.loadGrid(id);
        if (!grid) {
          navigate('/home');
          return;
        }

        setGridId(id);
        setTitle(grid.title);
        setLoading(false); // Grid metadata loaded immediately
        
        // Set as active grid in parallel (don't wait for this)
        userStorage.setActiveGrid(id).catch(error => {
          console.error('Failed to set active grid:', error);
        });
        
      } catch (error) {
        console.error('Error loading grid:', error);
        navigate('/home');
      }
    };

    loadGrid();
  }, [id, navigate, userStorage, userStorage?.isFirebaseReady]);

  // Reset data ready state when grid ID changes
  useEffect(() => {
    setDataReady(false);
  }, [gridId]);

  const handleCellSelected = (cellInfo: SelectedCellInfo | null) => {
    // console.log('Selected cell changed:', cellInfo);
  };

  const handleAddColumn = () => {
    // console.log('Column added from DataGrid');
  };

  // Handle title changes from both PageHeader and DataGrid
  const handleTitleChange = useCallback(async (newTitle: string) => {
    setTitle(newTitle);
    
    // Save the title change to Firestore
    if (userStorage && gridId) {
      try {
        // Use the more efficient title update function
        await userStorage.updateTitle(gridId, newTitle);
        
        // Trigger sidebar refresh to show the updated title
        onSidebarRefresh();
        
      } catch (error) {
        console.error('Error saving title change:', error);
        // Revert the local title state if save failed
        setTitle(title);
      }
    } else {
      console.warn('Cannot save title: userStorage or gridId not available', { userStorage: !!userStorage, gridId });
    }
  }, [userStorage, gridId, onSidebarRefresh, title]);

  // Callback when DataGrid is ready with data
  const handleDataGridReady = useCallback(() => {
    setDataReady(true);
  }, []);

  // Undo/Redo handlers for PageHeader
  const handleUndo = () => {
    if (undoHandlerRef.current) {
      undoHandlerRef.current();
    }
  };

  const handleRedo = () => {
    if (redoHandlerRef.current) {
      redoHandlerRef.current();
    }
  };

  // Set handlers from DataGrid
  const setUndoHandler = (handler: () => void) => {
    undoHandlerRef.current = handler;
  };

  const setRedoHandler = (handler: () => void) => {
    redoHandlerRef.current = handler;
  };

  // Handle selection changes from DataGrid
  const handleSelectionChanged = useCallback((count: number, rows: any[]) => {
    setSelectedRowsCount(count);
    setSelectedRows(rows);
  }, []);

  // Handle column config changes from DataGrid
  const handleColumnConfigsChanged = useCallback((configs: any[]) => {
    setColumnConfigs(configs);
  }, []);

  // Reference to DataGrid's updateRowData method
  const dataGridUpdateRef = useRef<((rowId: string | number, updatedData: Record<string, any>) => void) | null>(null);

  // Reference to DataGrid's deselect method
  const dataGridDeselectRef = useRef<(() => void) | null>(null);

  // Handle enrichment completion from PageHeader
  const handleEnrichmentComplete = useCallback((rowId: string | number, updatedData: Record<string, any>) => {
    // Update the DataGrid with the enriched data
    if (dataGridUpdateRef.current) {
      dataGridUpdateRef.current(rowId, updatedData);
    }
  }, []);

  // Set the DataGrid update handler
  const setDataGridUpdateHandler = useCallback((handler: (rowId: string | number, updatedData: Record<string, any>) => void) => {
    dataGridUpdateRef.current = handler;
  }, []);

  // Set the DataGrid deselect handler
  const setDataGridDeselectHandler = useCallback((handler: () => void) => {
    dataGridDeselectRef.current = handler;
  }, []);

  // Handle deselect all from PageHeader
  const handleDeselectAll = useCallback(() => {
    if (dataGridDeselectRef.current) {
      dataGridDeselectRef.current();
    }
  }, []);

  // Handle add to templates from PageHeader
  const handleAddToTemplates = useCallback(async () => {
    posthog?.capture('template_creation_started', {
      sheet_id: gridId,
      sheet_title: title
    });
    setTemplateModalOpen(true);
  }, [posthog, gridId, title]);

  const handleTemplateSave = useCallback(async (name: string, description: string, isGlobal = false) => {
    try {
      if (!userStorage || !gridId) return;
      
      setTemplateSaving(true);
      
      // Load the current grid data
      const currentGrid = await userStorage.loadGrid(gridId);
      if (!currentGrid) {
        throw new Error('Could not load grid data for template creation');
      }
      
      // Update the grid with the new name
      const gridForTemplate = {
        ...currentGrid,
        title: name
      };
      
      // Save to appropriate template collection
      if (isGlobal) {
        await userStorage.saveToGlobalTemplates(gridForTemplate, description, 'General');
        posthog?.capture('template_created', {
          template_name: name,
          template_type: 'global',
          source_sheet_id: gridId,
          description_length: description.length
        });
        toast.success('Global template created successfully!');
      } else {
        await userStorage.saveToMyTemplates(gridForTemplate, description, 'Personal');
        posthog?.capture('template_created', {
          template_name: name,
          template_type: 'personal',
          source_sheet_id: gridId,
          description_length: description.length
        });
        toast.success('Template created successfully!');
      }
      
      setTemplateModalOpen(false);
    } catch (error) {
      console.error('Error creating template:', error);
      posthog?.capture('template_creation_failed', {
        error: String(error),
        template_name: name,
        template_type: isGlobal ? 'global' : 'personal'
      });
      toast.error('Failed to create template. Please try again.');
    } finally {
      setTemplateSaving(false);
    }
  }, [userStorage, gridId, posthog]);

  // Only show loading while waiting for grid metadata or Firebase to be ready
  if (loading || !userStorage?.isFirebaseReady) {
    return (
      <div className="sheet-container">
        <div className="sheet-loading">
          <div className="spinner" />
          <p>Loading grid...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet-container">
      <PageHeader 
        title={title}
        onTitleChange={handleTitleChange}
        sidebarVisible={sidebarVisible}
        onShowSidebar={onShowSidebar}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        selectedRowsCount={selectedRowsCount}
        selectedRows={selectedRows}
        columnConfigs={columnConfigs}
        onEnrichmentComplete={handleEnrichmentComplete}
        onDeselectAll={handleDeselectAll}
        onAddToTemplates={handleAddToTemplates}
        onSubscriptionRefresh={onSubscriptionRefresh}
      />
      <div className="sheet-content">
        {/* Show DataGrid with its own loading state */}
        {!dataReady && (
          <div className="datagrid-loading-overlay">
            <div className="spinner" />
            <p>Loading spreadsheet data...</p>
          </div>
        )}
        <div style={{ opacity: dataReady ? 1 : 0, transition: 'opacity 0.2s' }}>
          <DataGrid 
            onCellSelected={handleCellSelected}
            onAddColumn={handleAddColumn}
            onSetUndoHandler={setUndoHandler}
            onSetRedoHandler={setRedoHandler}
            onUndoRedoStateChange={(undoable, redoable) => {
              setCanUndo(undoable);
              setCanRedo(redoable);
            }}
            activeGridId={gridId}
            onTitleChange={handleTitleChange}
            onDataReady={handleDataGridReady}
            onSelectionChanged={handleSelectionChanged}
            onColumnConfigsChanged={handleColumnConfigsChanged}
            onSetUpdateHandler={setDataGridUpdateHandler}
            onSetDeselectHandler={setDataGridDeselectHandler}
            onSubscriptionRefresh={onSubscriptionRefresh}
          />
        </div>
      </div>
      
      <TemplateModal
        isOpen={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSave={handleTemplateSave}
        initialName={title}
        loading={templateSaving}
      />
    </div>
  );
};

export default Sheet; 